import { useState } from 'react';
import { Search, User as UserIcon, ShieldCheck, Mail, Loader2, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { usePlatformUsers, useUpdateUserRole, type PlatformUser } from '@/hooks/usePlatform';
import { useAlert, useConfirm } from '@/components/ConfirmDialog';
import { useMe } from '@/hooks/useAuth';
import { useImpersonate } from '@/hooks/useImpersonation';
import { formatDate, formatNumber } from '@/lib/utils';
import { cn } from '@/lib/utils';

export function PlatformUsersPage() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'' | 'owner' | 'user'>('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const me = useMe();

  const { data, isPending, isFetching } = usePlatformUsers({
    search: debouncedSearch || undefined,
    role: roleFilter || undefined,
  });
  const updateRole = useUpdateUserRole();
  const confirm = useConfirm();
  const alertDialog = useAlert();
  const impersonate = useImpersonate();

  // debounce search
  const onSearchChange = (value: string) => {
    setSearch(value);
    const t = setTimeout(() => setDebouncedSearch(value), 300);
    return () => clearTimeout(t);
  };

  const onRoleToggle = async (u: PlatformUser) => {
    const next = u.platform_role === 'owner' ? 'user' : 'owner';
    const ok = await confirm({
      title: 'Rol değişikliği',
      description:
        next === 'owner'
          ? `${u.email} platform owner yetkisi alacak — tüm organizasyonlara erişim, impersonation, manuel bakiye grant yapabilir.`
          : `${u.email} normal kullanıcıya düşürülecek — platform yönetim paneline erişimi kalkar.`,
      confirmText: 'Değiştir',
      danger: next === 'user',
    });
    if (!ok) return;
    updateRole.mutate({ id: u.id, platform_role: next });
  };

  const onImpersonate = async (u: PlatformUser) => {
    if (u.id === me.data?.user.id) return;
    const ok = await confirm({
      title: `${u.email} olarak giriş yap?`,
      description:
        'Bu hesap olarak siteyi göreceksin. Üstteki sarı banner\'dan admine dönebilirsin. İşlem audit log\'a yazılır.',
      confirmText: 'Giriş Yap',
      danger: false,
    });
    if (!ok) return;
    try {
      await impersonate.mutateAsync(u.id);
      // Hard reload to /dashboard — identity just changed; every
      // mounted component that read /me under the old session needs
      // a clean remount so nothing flashes the admin email.
      window.location.href = '/dashboard';
    } catch (err) {
      await alertDialog({
        title: 'Giriş yapılamadı',
        description: err instanceof Error ? err.message : 'Beklenmedik hata',
        danger: true,
      });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <UserIcon className="w-5 h-5" /> Kullanıcılar
        </h1>
        <p className="text-muted-foreground text-sm">Platform geneli kullanıcı yönetimi.</p>
      </div>

      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="E-posta veya isim ara"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-8"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as '' | 'owner' | 'user')}
            className="flex h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">Tüm roller</option>
            <option value="owner">Platform Owner</option>
            <option value="user">Kullanıcı</option>
          </select>
          <div className="ml-auto text-xs text-muted-foreground">
            {isFetching ? 'Yenileniyor…' : `${formatNumber(data?.total ?? 0)} toplam`}
          </div>
        </CardContent>
      </Card>

      {isPending ? (
        <div className="text-muted-foreground">Yükleniyor…</div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b">
                <tr>
                  <th className="text-left py-2 px-3 font-medium">Kullanıcı</th>
                  <th className="text-left py-2 px-3 font-medium">Rol</th>
                  <th className="text-left py-2 px-3 font-medium">Org üyelikleri</th>
                  <th className="text-left py-2 px-3 font-medium">E-posta doğrulama</th>
                  <th className="text-left py-2 px-3 font-medium">Kayıt</th>
                  <th className="text-right py-2 px-3 font-medium">Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {data?.users.map((u) => (
                  <tr key={u.id} className="border-b last:border-b-0 hover:bg-muted/30">
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                          {u.avatar_url ? (
                            <img src={u.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                          ) : (
                            <UserIcon className="w-3.5 h-3.5 text-muted-foreground" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{u.full_name ?? u.email.split('@')[0]}</div>
                          <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <RoleBadge role={u.platform_role} />
                    </td>
                    <td className="py-2 px-3">{u.membership_count}</td>
                    <td className="py-2 px-3 text-xs">
                      {u.email_verified_at ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                          <ShieldCheck className="w-3.5 h-3.5" /> Doğrulandı
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <Mail className="w-3.5 h-3.5" /> Bekliyor
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">{formatDate(u.created_at)}</td>
                    <td className="py-2 px-3 text-right">
                      {me.data?.user.id !== u.id && (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onImpersonate(u)}
                            disabled={impersonate.isPending}
                            title="Bu kullanıcı olarak giriş yap"
                          >
                            <LogIn className="w-3.5 h-3.5" /> Giriş Yap
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onRoleToggle(u)}
                            disabled={updateRole.isPending}
                          >
                            {updateRole.isPending ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : u.platform_role === 'owner' ? (
                              'Kullanıcı yap'
                            ) : (
                              'Owner yap'
                            )}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {data?.users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-muted-foreground">
                      Kullanıcı bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: PlatformUser['platform_role'] }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        role === 'owner'
          ? 'bg-primary/10 text-primary'
          : 'bg-muted text-muted-foreground',
      )}
    >
      {role === 'owner' ? 'Platform Owner' : 'Kullanıcı'}
    </span>
  );
}
